"use client";

import { NativeLink as Link } from "@/components/native-link";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2,
  Copy,
  Database,
  Download,
  FileText,
  Globe2,
  LockKeyhole,
  Monitor,
  MoreVertical,
} from "lucide-react";
import { Card, Pill } from "@/components/pulse-shell";
import { api, type AuditEventDetail } from "@/lib/api";

function formatWhen(iso?: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

function initials(display?: string | null, email?: string | null) {
  const s = String(display || email || "?").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function riskTone(risk?: string | null): "gray" | "orange" | "red" {
  const r = String(risk || "").toLowerCase();
  if (r === "critical" || r === "high") return "red";
  if (r === "medium") return "orange";
  return "gray";
}

function capitalize(s?: string | null) {
  const t = String(s || "");
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function changeRows(before?: Record<string, unknown> | null, after?: Record<string, unknown> | null) {
  const keys = new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ]);
  return [...keys].map((k) => ({
    key: k,
    before: before && k in before ? JSON.stringify(before[k]) : "—",
    after: after && k in after ? JSON.stringify(after[k]) : "—",
  }));
}

function resourceHref(ev: AuditEventDetail): string | null {
  if (ev.sessionId) return `/sessions/${encodeURIComponent(ev.sessionId)}`;
  if (ev.agentId) return `/agents?agent=${encodeURIComponent(ev.agentId)}`;
  if (ev.resourceType === "session" && ev.resourceId) {
    return `/sessions/${encodeURIComponent(ev.resourceId)}`;
  }
  if (ev.resourceType === "agent" && ev.resourceId) {
    return `/agents?agent=${encodeURIComponent(ev.resourceId)}`;
  }
  return null;
}

export function AuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [payloadTab, setPayloadTab] = useState("JSON");
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [event, setEvent] = useState<AuditEventDetail | null>(null);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2300);
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const out = await api.getAuditEvent(id);
        if (!cancelled) setEvent(out.event);
      } catch (e) {
        if (!cancelled) {
          setEvent(null);
          setError(e instanceof Error ? e.message : "Event not found");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const changes = useMemo(
    () => changeRows(event?.before || null, event?.after || null),
    [event],
  );
  const jsonText = useMemo(
    () => JSON.stringify(event?.payload || event || {}, null, 2),
    [event],
  );
  const rawText = useMemo(() => {
    if (!event) return "";
    return [
      event.eventId,
      event.action,
      event.actorEmail || event.actorId,
      event.resourceId || event.resourceLabel,
      event.environment || "production",
      event.outcome,
    ].join("|");
  }, [event]);

  if (loading) {
    return (
      <div className="page-head">
        <p className="page-subtitle">Loading audit event…</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="page-head">
        <div className="eyebrow">
          <Link href="/audit">Audit log</Link> / {id}
        </div>
        <h1 className="page-title">Event not found</h1>
        <p className="page-subtitle">{error || "This audit event id is unknown or outside retention."}</p>
      </div>
    );
  }

  const outcomeOk = !["denied", "failure", "failed"].includes(String(event.outcome || "").toLowerCase());
  const href = resourceHref(event);
  const publishChecks = event.publishChecks && typeof event.publishChecks === "object"
    ? event.publishChecks
    : null;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link href="/audit">Audit log</Link> / {event.eventId}
          </div>
          <div className="incident-detail-title">
            <h1 className="page-title">{event.title || event.action}</h1>
            <Pill tone={riskTone(event.risk)}>
              <span className="dot" />
              {capitalize(event.risk)} risk
            </Pill>
          </div>
          <p className="page-subtitle">{formatWhen(event.createdAt)}</p>
        </div>
        <div className="head-actions">
          <button
            className="btn"
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(event.eventId);
              flash("Event ID copied");
            }}
          >
            <Copy />
            Copy event ID
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => {
              const blob = new Blob([jsonText], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${event.eventId}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            <Download />
            Download JSON
          </button>
          <button className="icon-button" type="button" aria-label="More">
            <MoreVertical />
          </button>
        </div>
      </div>

      <section className="card audit-detail-strip">
        <DCell
          icon={<span className="avatar">{initials(event.actorDisplay, event.actorEmail)}</span>}
          label="Actor"
          value={event.actorDisplay || event.actorEmail || "Unknown actor"}
          plain
        />
        <DCell icon={<FileText />} label="Action" value={event.action} />
        <DCell
          icon={<Database />}
          label="Resource"
          value={
            event.resourceLabel
              ? (event.resourceType ? `${event.resourceLabel} · ${event.resourceType}` : event.resourceLabel)
              : "—"
          }
        />
        <DCell icon={<Globe2 />} label="Environment" value={capitalize(event.environment || "production")} />
        <DCell
          icon={<CheckCircle2 />}
          label="Result"
          value={outcomeOk ? "Success" : capitalize(event.outcome)}
          green={outcomeOk}
        />
        <DCell
          icon={<Monitor />}
          label="Source"
          value={[event.source === "web" ? "Web UI" : event.source || "—", event.sourceIp]
            .filter(Boolean)
            .join(" · ")}
        />
      </section>

      <div className="audit-detail-grid">
        <div className="stack">
          {changes.length > 0 ? (
            <Card
              title="Change summary"
              action={
                <span className="muted">
                  {changes.length} field{changes.length === 1 ? "" : "s"}
                </span>
              }
            >
              <table className="change-table">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Before</th>
                    <th>After</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((row) => (
                    <tr key={row.key}>
                      <td>{row.key}</td>
                      <td>{row.before}</td>
                      <td>
                        <b>{row.after}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : null}

          {publishChecks ? (
            <Card title="Publish checks">
              <KV
                rows={Object.entries(publishChecks).map(([k, v]) => [k, String(v)])}
              />
            </Card>
          ) : null}

          <Card
            title="Canonical event payload"
            action={
              <div className="head-actions">
                <button
                  className="btn small"
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(jsonText);
                    flash("JSON copied");
                  }}
                >
                  <Copy />
                  Copy JSON
                </button>
              </div>
            }
          >
            <div className="payload-tabs">
              {["JSON", "Raw"].map((x) => (
                <button
                  key={x}
                  type="button"
                  className={payloadTab === x ? "active" : ""}
                  onClick={() => setPayloadTab(x)}
                >
                  {x}
                </button>
              ))}
            </div>
            <pre className="code-block">{payloadTab === "JSON" ? jsonText : rawText}</pre>
          </Card>

          {Array.isArray(event.relatedEventIds) && event.relatedEventIds.length > 0 ? (
            <Card title="Related event sequence">
              {event.relatedEventIds.map((rid) => (
                <Sequence key={rid} time="" label={rid} id={rid} />
              ))}
              <Sequence time="" label={event.title || event.action} id="This event" current />
            </Card>
          ) : null}
        </div>

        <div className="stack">
          <Card title="Actor">
            <div className="identity-head">
              <span className="avatar">{initials(event.actorDisplay, event.actorEmail)}</span>
              <span>
                <b>{event.actorDisplay || event.actorEmail || "Unknown actor"}</b>
                <span>{event.actorRole || event.actorEmail || "—"}</span>
              </span>
            </div>
            <KV
              rows={[
                ["Email", event.actorEmail || "—"],
                ["Role", event.actorRole || "—"],
                ["Source IP", event.sourceIp || "—"],
                ["User agent", event.userAgent || "—"],
                ["Correlation", event.correlationId || "—"],
                ["Actor ID", event.actorId || "—"],
              ]}
            />
          </Card>

          <Card title="Resource">
            <div className="identity-head">
              <span className="metric-icon">
                <Database />
              </span>
              <span>
                <b>{event.resourceLabel || "—"}</b>
                <span>{event.resourceType || "resource"}</span>
              </span>
              {href ? (
                <Link href={href} style={{ marginLeft: "auto", color: "#0968f2", fontSize: 12 }}>
                  Open resource →
                </Link>
              ) : null}
            </div>
            <KV
              rows={[
                ["Type", event.resourceType || "—"],
                ["Label", event.resourceLabel || "—"],
                ["Resource ID", event.resourceId || "—"],
                ["Session", event.sessionId || "—"],
                ["Agent", event.agentId || "—"],
              ]}
            />
          </Card>

          <Card title="Authorization">
            <KV
              rows={[
                [
                  "Allowed by",
                  event.permission ? `permission ${event.permission}` : "Route permission check",
                ],
                ["Outcome", capitalize(event.outcome || "success")],
                ["Category", event.category || "—"],
              ]}
              green={outcomeOk}
            />
          </Card>

          <Card title="Integrity">
            <p className="muted" style={{ margin: 0 }}>
              Cryptographic ledger verify is out of product scope. Events are append-only in the metrics store.
            </p>
          </Card>

          <div className="immutable-note">
            <LockKeyhole size={20} />
            <span>
              <b>This event is immutable.</b>
              Corrections create new linked events.
            </span>
          </div>
        </div>
      </div>

      {toast ? (
        <div className="toast">
          <span className="toast-icon">✓</span>
          <span>
            <b>{toast}</b>
            <span>Copied to clipboard.</span>
          </span>
        </div>
      ) : null}
    </>
  );
}

function DCell({
  icon,
  label,
  value,
  plain,
  green,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  plain?: boolean;
  green?: boolean;
}) {
  return (
    <div className="audit-detail-cell">
      <span
        className={plain ? "" : "summary-icon"}
        style={green ? { color: "#0aa878", background: "#eaf9f3" } : undefined}
      >
        {icon}
      </span>
      <span>
        {label}
        <b>{value}</b>
      </span>
    </div>
  );
}

function KV({ rows, green }: { rows: string[][]; green?: boolean }) {
  return (
    <table className="kv-table">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td>{k}</td>
            <td style={green && v !== "None" && v !== "—" ? { color: "#07805d" } : undefined}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Sequence({
  time,
  label,
  id,
  current,
}: {
  time: string;
  label: string;
  id: string;
  current?: boolean;
}) {
  return (
    <div className={`event-sequence-row ${current ? "current" : ""}`}>
      <time>{time || "—"}</time>
      <span>{label}</span>
      {current ? (
        <span style={{ textAlign: "right" }}>This event</span>
      ) : (
        <Link href={`/audit/${encodeURIComponent(id)}`}>{id} ↗</Link>
      )}
    </div>
  );
}
